import React, { useCallback, useEffect, useRef, useState } from 'react';
import store from './journalStore';

/* The "a few of my favourite moments" film strip on the Us tab.
   Instead of a fixed set of images, it pulls every photo from our gallery and
   keeps four insta-film frames on screen, swapping one at a time on a gentle
   loop — like a living slideshow. The white polaroid frame + cute Caveat
   caption are kept exactly as designed. Falls back to the static favourites if
   the gallery is empty, and holds still when the viewer prefers reduced motion. */

const SLOTS = 4;
const SWAP_MS = 3400;

// Stable per-slot tilt so frames don't jitter on every swap.
const ROTS = [-3.2, 2.4, -1.8, 3.0];

const CUTE = [
  'us 💕', 'my favourite 😍', 'always 🥰', 'my home 🏡',
  'partners in crime 🤝', 'forever 💍', 'my person 🫶', 'sunshine ☀️',
  'mine 💖', 'best day 🌸', 'you & me 💞', 'endless 🔁',
];

const reducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const shuffle = (arr) => {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// A real caption if the photo has one, else a stable cute line (same photo
// always gets the same fallback, so it doesn't flicker between renders).
const captionOf = (item) => {
  if (!item) return '';
  if (item.src) return item.caption || ''; // static fallback item
  if (item.caption && item.caption.trim()) return item.caption.trim();
  let h = 0;
  const s = String(item.id || '');
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return CUTE[h % CUTE.length];
};

export default function MomentsFilm({ passcode, fallback = [] }) {
  const [slots, setSlots] = useState(() => fallback.slice(0, SLOTS));
  const [urls, setUrls] = useState({}); // gallery photo id -> object URL
  const [ready, setReady] = useState(false);

  const orderRef = useRef([]); // the shuffled rotation pool
  const nextRef = useRef(0); // next photo to bring in
  const cursorRef = useRef(0); // slot to replace next (round-robin)
  const urlsRef = useRef({});
  const pendingRef = useRef(new Set());

  // Fetch + cache a photo's bytes once, so swaps are instant (no skeleton flash).
  const ensureUrl = useCallback(async (item) => {
    if (!item || item.src || !item.id) return; // static or invalid
    const { id } = item;
    if (urlsRef.current[id] || pendingRef.current.has(id)) return;
    pendingRef.current.add(id);
    try {
      const u = await store.photoUrl(passcode, id);
      urlsRef.current[id] = u;
      setUrls((m) => ({ ...m, [id]: u }));
    } catch (e) {
      /* leave it — the frame keeps a soft placeholder */
    } finally {
      pendingRef.current.delete(id);
    }
  }, [passcode]);

  // Load the gallery; fall back to the static favourites if it's empty.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let pool = [];
      try {
        pool = await store.photos(passcode);
      } catch (e) {
        pool = [];
      }
      if (cancelled) return;
      orderRef.current = pool && pool.length ? shuffle(pool) : fallback.slice();
      const order = orderRef.current;
      if (!order.length) {
        setReady(true);
        return;
      }
      const count = Math.min(SLOTS, order.length);
      const init = order.slice(0, count);
      nextRef.current = count % order.length;
      cursorRef.current = 0;
      setSlots(init);
      init.forEach(ensureUrl);
      ensureUrl(order[nextRef.current]);
      setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [passcode, fallback, ensureUrl]);

  // Gentle slideshow: replace one frame at a time, round-robin.
  useEffect(() => {
    if (!ready) return undefined;
    const order = orderRef.current;
    if (reducedMotion() || order.length <= SLOTS) return undefined; // nothing to cycle
    const t = setInterval(() => {
      const ord = orderRef.current;
      if (!ord.length) return;
      const incoming = ord[nextRef.current];
      nextRef.current = (nextRef.current + 1) % ord.length;
      const slot = cursorRef.current;
      cursorRef.current = (cursorRef.current + 1) % SLOTS;
      ensureUrl(incoming);
      ensureUrl(ord[nextRef.current]); // prefetch the next so it's ready
      setSlots((prev) => {
        const copy = prev.slice();
        copy[slot] = incoming;
        return copy;
      });
    }, SWAP_MS);
    return () => clearInterval(t);
  }, [ready, ensureUrl]);

  // Revoke cached object URLs on unmount.
  useEffect(() => () => {
    Object.values(urlsRef.current).forEach((u) => {
      if (typeof u === 'string' && u.startsWith('blob:')) URL.revokeObjectURL(u);
    });
    urlsRef.current = {};
  }, []);

  if (!slots.length) return null;

  return (
    <div className="anusha-gallery anusha-gallery-film">
      {slots.map((item, i) => {
        const src = item && (item.src || urls[item.id]);
        return (
          // eslint-disable-next-line react/no-array-index-key
          <figure className="anusha-polaroid" key={`film-slot-${i}`} style={{ '--rot': `${ROTS[i % ROTS.length]}deg` }}>
            {src ? (
              <img
                key={item.src || item.id}
                className="anusha-film-img"
                src={src}
                alt={`our moment — ${captionOf(item)}`}
                loading="lazy"
                draggable={false}
              />
            ) : (
              <div className="anusha-film-skel" />
            )}
            <figcaption>{captionOf(item)}</figcaption>
          </figure>
        );
      })}
    </div>
  );
}
